/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: S1/S2 marker filtering and cardiac-cycle construction
// (core-viewer.js 2429-2604, 2895-2907).
// The prototype's module-scope `phaseReviewResolution` global becomes an explicit
// argument to visualGuidePeaks/visualTimingCycles.
// rawSpikeRatio (2490-2495) is dead in the prototype (no call sites) and is dropped.

import { mean, median } from "./stats"
import {
  MIN_ENERGY_RELIABLE_CYCLES,
  MIN_ENERGY_HIGH_CONF,
  MAX_ENERGY_LOW_CONF,
  MAX_ENERGY_LOW_CONF_RATIO,
} from "./constants"

export function clinicalPeaks(payload) {
  return (payload.peaks || []).filter((p) => !p.guideOnly)
}

export function visualGuidePeaks(payload, phaseReviewResolution) {
  const peaks = payload.peaks || []
  if (phaseReviewResolution !== "alternate") return peaks
  return peaks.map((p) => ({
    ...p,
    l: p.l === "S1" ? "S2" : p.l === "S2" ? "S1" : p.l,
  }))
}

export function intervalAt(payload, phaseReviewResolution, t) {
  const peaksForQuality = visualGuidePeaks(payload, phaseReviewResolution)
  for (let i = 0; i < peaksForQuality.length - 1; i++) {
    const a = peaksForQuality[i],
      b = peaksForQuality[i + 1]
    if (t >= a.t && t <= b.t) {
      if (a.l === "S1" && b.l === "S2") return { kind: "Systole", short: "SYS", start: a, end: b, duration: b.t - a.t }
      if (a.l === "S2" && b.l === "S1") return { kind: "Diastole", short: "DIA", start: a, end: b, duration: b.t - a.t }
    }
  }
  return null
}

export function timingCyclesForPeaks(peaks) {
  const cycles = []
  if (!peaks || peaks.length < 3) return cycles
  for (let i = 0; i < peaks.length - 2; i++) {
    const a = peaks[i],
      b = peaks[i + 1],
      c = peaks[i + 2]
    if (a.l === "S1" && b.l === "S2" && c.l === "S1") {
      const sys = b.t - a.t,
        dia = c.t - b.t,
        cyc = c.t - a.t
      if (sys > 0 && dia > 0 && cyc > 0) {
        cycles.push({
          n: cycles.length + 1,
          markerIndex: i,
          cycleId: `cycle_${cycles.length + 1}_${a.t.toFixed(4)}_${b.t.toFixed(4)}_${c.t.toFixed(4)}`,
          s1: a,
          s2: b,
          nextS1: c,
          sys,
          dia,
          cycle: cyc,
          ratio: sys / dia,
          sysFrac: sys / cyc,
          confidence: Math.min(a.confidence ?? 1, b.confidence ?? 1, c.confidence ?? 1),
          inferredCount: [a, b, c].filter((p) => p.source === "inferred").length,
          metricInferredCount: [a, b, c].filter((p) => p.source === "inferred" && !p.guideOnly).length,
          visualOnlyInferredCount: [a, b, c].filter((p) => p.source === "inferred" && p.guideOnly).length,
          outsideEvalMarkerCount: [a, b, c].filter((p) => p.outsideEvalRange === true || p.usedForResults === false)
            .length,
        })
      }
    }
  }
  return cycles
}

export function cycleAuditId(cycle) {
  if (!cycle) return null
  return (
    cycle.cycleId ||
    `cycle_${cycle.n || "?"}_${cycle.s1.t.toFixed(4)}_${cycle.s2.t.toFixed(4)}_${cycle.nextS1.t.toFixed(4)}`
  )
}

export function cycleAuditIds(cycles) {
  return (cycles || []).map(cycleAuditId).filter(Boolean)
}

export function timingCycles(payload) {
  return timingCyclesForPeaks(clinicalPeaks(payload))
}

export function visualTimingCycles(payload, phaseReviewResolution) {
  return timingCyclesForPeaks(visualGuidePeaks(payload, phaseReviewResolution))
}

export function localNoiseReason(cycle, cycleMed) {
  const reasons = []
  if (cycle.cycle < 0.32 || cycle.cycle > 1.8) reasons.push("cycle length")
  if (cycle.sys < 0.08 || cycle.sys > 0.7) reasons.push("sys timing")
  if (cycle.dia < 0.12 || cycle.dia > 1.55) reasons.push("dia timing")
  if (cycleMed && Math.abs(cycle.cycle - cycleMed) > 0.38 * cycleMed) reasons.push("rhythm instability")
  return reasons
}

export function mergeWindows(windows) {
  if (!windows.length) return []
  windows = [...windows].sort((a, b) => a.start - b.start)
  const out = [windows[0]]
  for (const w of windows.slice(1)) {
    const last = out[out.length - 1]
    if (w.start <= last.end + 0.08) {
      last.end = Math.max(last.end, w.end)
      last.reasons = [...new Set([...(last.reasons || []), ...(w.reasons || [])])]
    } else out.push({ ...w })
  }
  return out
}

export function longestUsableRun(cycles) {
  let best = [],
    cur = []
  cycles.forEach((c) => {
    if (c.usable) {
      cur.push(c)
      if (cur.length > best.length) best = cur
    } else cur = []
  })
  return best
}

export function lowConfidenceMarker(p) {
  return p.guideOnly || p.source === "inferred" || (p.confidence ?? 1) < 0.7
}

export function longestReliableRun(cycles) {
  let best = [],
    cur = []
  const markerQualityOk = (run) => {
    if (run.length < MIN_ENERGY_RELIABLE_CYCLES) return false
    const markers = []
    const seen = new Set()
    run.forEach((c) =>
      [c.s1, c.s2, c.nextS1].forEach((p) => {
        const k = `${p.l}:${p.t.toFixed(4)}`
        if (!seen.has(k)) {
          seen.add(k)
          markers.push(p)
        }
      })
    )
    if (!markers.length) return false
    const lowCount = markers.filter(lowConfidenceMarker).length
    const avgConfidence = mean(markers.map((p) => p.confidence ?? 1))
    const proportionalLowLimit = Math.max(2, Math.floor(markers.length * MAX_ENERGY_LOW_CONF_RATIO))
    const lowLimit = Math.min(MAX_ENERGY_LOW_CONF, proportionalLowLimit)
    return lowCount <= lowLimit && avgConfidence >= MIN_ENERGY_HIGH_CONF
  }
  const consider = (run) => {
    for (let len = run.length; len >= MIN_ENERGY_RELIABLE_CYCLES; len--) {
      for (let i = 0; i <= run.length - len; i++) {
        const candidate = run.slice(i, i + len)
        if (markerQualityOk(candidate)) {
          if (candidate.length > best.length) best = candidate
          return
        }
      }
    }
  }
  cycles.forEach((c) => {
    if (c.usable) cur.push(c)
    else {
      consider(cur)
      cur = []
    }
  })
  consider(cur)
  return best
}

export function windowCoveragePct(windows, duration) {
  if (!duration || !windows.length) return 0
  const total = windows.reduce((acc, w) => acc + Math.max(0, Math.min(duration, w.end) - Math.max(0, w.start)), 0)
  return Math.min(100, (100 * total) / duration)
}

export function cycleRunDuration(cycles) {
  if (!cycles || !cycles.length) return 0
  return Math.max(0, cycles[cycles.length - 1].nextS1.t - cycles[0].s1.t)
}

export function cycleTotalDuration(cycles) {
  if (!cycles || !cycles.length) return 0
  return cycles.reduce((acc, c) => acc + Math.max(0, c.cycle || 0), 0)
}

export function regularPctForCycles(cycles) {
  if (!cycles || !cycles.length) return null
  const lengths = cycles.map((c) => c.cycle)
  const med = median(lengths)
  if (!med) return null
  const close = lengths.filter((v) => Math.abs(v - med) <= 0.1 * med).length
  return (100 * close) / lengths.length
}

export function markerConfidenceForCycles(cycles) {
  if (!cycles || !cycles.length) return 0
  const markers = []
  const seen = new Set()
  cycles.forEach((c) =>
    [c.s1, c.s2, c.nextS1].forEach((p) => {
      const k = `${p.l}:${p.t.toFixed(4)}`
      if (!seen.has(k)) {
        seen.add(k)
        markers.push(p)
      }
    })
  )
  if (!markers.length) return 0
  return mean(markers.map((p) => p.confidence ?? 1))
}

export function segmentRangeForCycles(cycles) {
  if (!cycles || !cycles.length) return null
  const start = cycles[0].s1.t
  const end = cycles[cycles.length - 1].nextS1.t
  return { start, end, duration: Math.max(0, end - start) }
}

export function canonicalTimingDurationSummary(cycles) {
  if (!Array.isArray(cycles) || !cycles.length) return null
  const meanSystolicSeconds = cycles.reduce((sum, cycle) => sum + Number(cycle.sys || 0), 0) / cycles.length
  const meanDiastolicSeconds = cycles.reduce((sum, cycle) => sum + Number(cycle.dia || 0), 0) / cycles.length
  if (!(meanSystolicSeconds > 0) || !(meanDiastolicSeconds > 0)) return null
  return {
    method: "ratio_of_mean_accepted_cycle_durations",
    meanSystolicMsExact: meanSystolicSeconds * 1000,
    meanDiastolicMsExact: meanDiastolicSeconds * 1000,
    sdRatioExact: meanSystolicSeconds / meanDiastolicSeconds,
    selectedTimingCycleIds: cycles.map(cycleAuditId).filter(Boolean),
  }
}
